import { Form, InlineGrid, Modal, TextField, Text, Banner, Checkbox, BlockStack } from '@shopify/polaris';
import { lengthLessThan, lengthMoreThan, notEmpty, useField, useForm } from '@shopify/react-form';
import QuickUploadImage from 'components/oneclick-upload-image';
import TextFieldRichText from 'components/TextFieldRichText';
import __helpers from 'helpers/index';
import helpers from 'helpers/index';
import { TypedProductCategory } from 'interface/product.model';
import __ from 'languages/index';
import { useNotification } from 'context/NotificationContext';
import { useCreateProductCategory, useUpdateProductCategory } from 'queries/product_category.query';
import React, { useState, useEffect, useCallback } from 'react';
export default function ProductCategoryModalAddAndEdit({
  show = false,
  onClose = null,
  entity = null
}: {
  show: boolean;
  entity?: TypedProductCategory;
  onClose?: (entity?: TypedProductCategory) => void;
}) {
  const [internalError, setInternalError] = useState('');
  const [open, setOpen] = useState(false);
  const {
    addNotification
  } = useNotification();
  const {
    mutateAsync: updateEntity,
    isSuccess: updatedSuccess
  } = useUpdateProductCategory();
  const {
    mutateAsync: createEntity,
    isSuccess: createdSuccess
  } = useCreateProductCategory();

  // const dispatch = useAppDispatch();

  useEffect(() => {
    if (show) setOpen(true);
  }, [show]);
  useEffect(() => {
    if (updatedSuccess) {
      addNotification('info', 'Đã cập nhật chuyên mục');
    }
  }, [updatedSuccess]);
  useEffect(() => {
    if (createdSuccess) {
      addNotification('info', 'Đã tạo chuyên mục thành công');
    }
  }, [createdSuccess]);
  const onCloseCallback = useCallback((entity?: any) => {
    onClose(entity);
    setOpen(false);
  }, []);
  useEffect(() => {
    reset();
  }, []);
  const useFields = {
    category_thumbnail: useField<string>({
      value: entity?.category_thumbnail,
      validates: []
    }),
    category_thumbnail_cache: useField<string>({
      value: entity?.category_thumbnail_to_media ? entity?.category_thumbnail_to_media.media_thumbnail['256x169'] : undefined,
      validates: []
    }),
    category_name: useField<string>({
      value: entity?.category_name || '',
      validates: [notEmpty('không được để trống trường này'), lengthLessThan(150, 'Không được dài hơn 150 ký tự.'), lengthMoreThan(2, 'Trường này yêu cầu phải dài hơn 2 ký tự.')]
    }),
    category_slug: useField<string>({
      value: entity?.category_slug ?? '',
      validates: [lengthLessThan(150, 'Không được dài hơn 150 ký tự.'), lengthMoreThan(2, 'Trường này yêu cầu phải dài hơn 2 ký tự.'), inputValue => {
        if (helpers.isUTF8(inputValue)) {
          return 'UTF-8 can not be used here.';
        }
      }]
    }),
    category_excerpt: useField<string>({
      value: entity?.category_excerpt || '',
      validates: [lengthLessThan(255, 'Không được dài hơn 255 ký tự.')]
    }),
    category_description: useField<string>({
      value: entity?.category_description || '',
      validates: [lengthLessThan(65000, 'Không được dài hơn 65000 ký tự.')]
    }),
    category_tags: useField<string>({
      value: entity?.category_tags || '',
      validates: [lengthLessThan(255, 'Không được dài hơn 255 ký tự.')]
    }),
    category_status: useField<number>({
      value: entity?.category_status ?? 1,
      validates: []
    })
  };
  const {
    fields,
    submit,
    submitting,
    dirty,
    reset
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        let addProductCategory: Promise<any>;
        if (entity) {
          addProductCategory = await updateEntity({
            category_id: entity?.category_id,
            category_name: values.category_name,
            category_excerpt: values.category_excerpt,
            category_description: values.category_description,
            category_thumbnail: values.category_thumbnail,
            category_tags: values.category_tags,
            category_status: values.category_status,
            category_slug: values.category_slug
          });
        } else {
          addProductCategory = await createEntity({
            category_name: values.category_name,
            category_excerpt: values.category_excerpt,
            category_description: values.category_description,
            category_thumbnail: values.category_thumbnail,
            category_tags: values.category_tags,
            category_status: values.category_status,
            category_slug: values.category_slug
          });
        }
        let Results = await addProductCategory;
        onCloseCallback(Results);
        reset();
      } catch (e) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return {
        status: 'success'
      };
    }
  });
  const [uploadError, setUploadError] = useState(null);
  useEffect(() => {
    let slugText = helpers.slugify(useFields.category_name.value);
    if (slugText && !entity) {
      useFields.category_slug.onChange(slugText);
    }
  }, [useFields.category_name, entity]);
  return <Modal activator={null} open={open} onClose={() => onCloseCallback()} title={entity ? 'Cập nhật danh mục sản phẩm' : 'Thêm danh mục sản phẩm'} primaryAction={{
    content: entity ? __('update_label_button') : 'Lưu lại',
    loading: submitting,
    disabled: !dirty,
    onAction: submit
  }} secondaryActions={[{
    content: 'Đóng',
    disabled: submitting,
    onAction: () => onCloseCallback()
  }]}>
      <Modal.Section>
        {internalError && <>
            <Banner tone="critical" onDismiss={() => setInternalError(null)}>
              {internalError}
            </Banner>
            <br />
          </>}

        <Form onSubmit={submit}>
          <BlockStack gap="200">
            <InlineGrid columns={['oneThird', 'twoThirds']} gap={'200'}>
              <div className="d1">
                <QuickUploadImage width={180} height={180} onSuccess={e => {
                useFields.category_thumbnail.onChange(e.media_id);
                useFields.category_thumbnail_cache.onChange(e.media_url);
              }} onError={e => setUploadError(e)} title="" placeholder={__helpers.getMediaLink(useFields.category_thumbnail_cache.value, 'https://placehold.co/128x128')} />
                <Text as="p" tone="critical">
                  {uploadError}
                </Text>
              </div>
              <BlockStack gap="500">
                <TextField label="Tên danh mục" maxLength={150} showCharacterCount autoComplete="off" requiredIndicator {...fields.category_name} />
                <TextField label="Đường dẫn tĩnh" maxLength={150} showCharacterCount autoComplete="off" {...fields.category_slug} />
              </BlockStack>
            </InlineGrid>

            <TextField label="Mô tả ngắn" autoComplete="off" multiline={3} maxLength={255} showCharacterCount requiredIndicator {...fields.category_excerpt} />

            <TextField label="Từ khóa" autoComplete="off" helpText={'Phân tách các từ khóa bằng dấu phẩy.'} maxLength={255} showCharacterCount {...fields.category_tags} />

            <TextFieldRichText label={'Mô tả dài'} initialData={entity?.category_description} onCallback={content => useFields.category_description.onChange(content)} />

            <Checkbox label="Cho phép hiển thị" helpText="Nếu tắt, Category này sẽ không hiển thị trên ứng dụng hoặc website." checked={useFields.category_status.value === 1} onChange={e => {
            useFields.category_status.onChange(Number(e));
          }} />
          </BlockStack>
        </Form>
      </Modal.Section>
    </Modal>;
}